import * as React from 'react';
import { useState, useRef, useEffect, useCallback, useReducer } from 'react';
import { 
  Undo, Redo2, Pencil, Type as TextIcon, RotateCw, FlipHorizontal, 
  FlipVertical, Sliders, Eye, Eraser, Check, Plus, Minus, X, Download, Save
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Slider } from '../components/ui/slider';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';
// 🛠️ Importação da função MEDIA para resolver o link do servidor
import { MEDIA } from '../lib/utils';

// --- CONFIGURAÇÕES DE FILTROS ---
const FILTER_PRESETS = {
  natural: { brightness: 100, contrast: 100, saturate: 100, sepia: 0, grayscale: 0, hueRotate: 0, blur: 0 },
  cinematic: { brightness: 95, contrast: 145, saturate: 110, sepia: 15, grayscale: 0, hueRotate: 0, blur: 0 },
  cyberpunk: { brightness: 115, contrast: 135, saturate: 200, sepia: 0, grayscale: 0, hueRotate: 280, blur: 0 },
  vivid: { brightness: 110, contrast: 120, saturate: 150, sepia: 0, grayscale: 0, hueRotate: 0, blur: 0 }
};

const initialState = {
  history: [],
  historyIndex: -1,
  settings: FILTER_PRESETS.natural,
  zoom: 0.8,
  canvasSize: { width: 0, height: 0 },
  rotation: 0,
  flipH: false,
  flipV: false
};

function editorReducer(state, action) {
  switch (action.type) {
    case 'SET_IMAGE':
      const newHistory = state.history.slice(0, state.historyIndex + 1);
      return { ...state, history: [...newHistory, action.payload], historyIndex: newHistory.length };
    case 'UPDATE_SETTINGS':
      return { ...state, settings: { ...state.settings, ...action.payload } };
    case 'SET_CANVAS_SIZE':
      return { ...state, canvasSize: action.payload };
    case 'TRANSFORM':
      return { ...state, ...action.payload };
    case 'UNDO':
      return state.historyIndex > 0 ? { ...state, historyIndex: state.historyIndex - 1 } : state;
    case 'REDO':
      return state.historyIndex < state.history.length - 1 ? { ...state, historyIndex: state.historyIndex + 1 } : state;
    case 'SET_ZOOM':
      return { ...state, zoom: action.payload };
    default: return state;
  }
}

export default function ProfessionalPixelStudio({ initialImage, onClose, onSave }) {
  const [state, dispatch] = useReducer(editorReducer, initialState);
  const [activeTool, setActiveTool] = useState('filters');
  const [showOriginal, setShowOriginal] = useState(false);
  const [isDrawing, setIsDrawing] = useState(false);
  const [brushSettings, setBrushSettings] = useState({ color: '#14b8a6', size: 12, mode: 'source-over' });
  const [textInput, setTextInput] = useState({ text: '', size: 60, color: '#ffffff' });
  
  const canvasRef = useRef(null);

  // --- RENDERIZAÇÃO DO CANVAS ---
  const render = useCallback(() => {
    const imgSource = showOriginal ? state.history[0] : state.history[state.historyIndex];
    if (!canvasRef.current || !imgSource) return;
    
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    const img = new Image();
    img.crossOrigin = "anonymous";
    
    // 🖼️ APLICAÇÃO DO MEDIA: Garante que o canvas carregue a imagem do Render
    img.src = imgSource.startsWith('data:') ? imgSource : MEDIA(imgSource);

    img.onload = () => {
      canvas.width = state.canvasSize.width;
      canvas.height = state.canvasSize.height;
      
      ctx.save();
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      
      ctx.translate(canvas.width/2, canvas.height/2);
      ctx.rotate((state.rotation * Math.PI) / 180);
      ctx.scale(state.flipH ? -1 : 1, state.flipV ? -1 : 1);
      ctx.translate(-canvas.width/2, -canvas.height/2);

      if (!showOriginal) {
        const s = state.settings;
        ctx.filter = `brightness(${s.brightness}%) contrast(${s.contrast}%) saturate(${s.saturate}%) sepia(${s.sepia}%) grayscale(${s.grayscale}%) hue-rotate(${s.hueRotate}deg) blur(${s.blur}px)`;
      }
      
      ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
      ctx.restore();
    };
  }, [state.history, state.historyIndex, state.settings, showOriginal, state.canvasSize, state.rotation, state.flipH, state.flipV]);

  useEffect(() => { render(); }, [render]);

  useEffect(() => {
    if (initialImage) {
      const img = new Image();
      img.crossOrigin = "anonymous";
      img.src = MEDIA(initialImage);
      img.onload = () => {
        dispatch({ type: 'SET_CANVAS_SIZE', payload: { width: img.width, height: img.height } });
        dispatch({ type: 'SET_IMAGE', payload: MEDIA(initialImage) });
      };
    }
  }, [initialImage]);

  const getPointerPos = (e) => {
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    return {
      x: (e.clientX - rect.left) * (canvas.width / rect.width),
      y: (e.clientY - rect.top) * (canvas.height / rect.height)
    };
  };

  const handlePointerDown = (e) => {
    if (activeTool !== 'draw') return;
    setIsDrawing(true);
    const { x, y } = getPointerPos(e);
    const ctx = canvasRef.current.getContext('2d');
    ctx.beginPath();
    ctx.moveTo(x, y);
  };

  const handlePointerMove = (e) => {
    if (!isDrawing) return;
    const { x, y } = getPointerPos(e);
    const ctx = canvasRef.current.getContext('2d');
    ctx.globalCompositeOperation = brushSettings.mode;
    ctx.strokeStyle = brushSettings.color;
    ctx.lineWidth = brushSettings.size;
    ctx.lineCap = 'round';
    ctx.lineTo(x, y);
    ctx.stroke();
  };

  const handlePointerUp = () => {
    if (isDrawing) {
      setIsDrawing(false);
      dispatch({ type: 'SET_IMAGE', payload: canvasRef.current.toDataURL() });
    }
  };

  const handleSaveAndExit = () => {
    try {
      const finalImage = canvasRef.current.toDataURL('image/jpeg', 1.0);
      onSave(finalImage); 
    } catch (e) {
      toast.error("Erro ao exportar imagem.");
    }
  };

  return (
    // 🛠️ CORREÇÃO: "fixed inset-0" para preencher a tela corretamente
    <div className="fixed inset-0 z-[99999] bg-[#020202] text-white flex flex-col overflow-hidden pointer-events-auto select-none font-sans">
      
      <header className="h-20 border-b border-white/10 bg-black/95 flex items-center justify-between px-8 shrink-0 backdrop-blur-md">
        <div className="flex items-center gap-6">
          <div className="flex flex-col">
            <span className="font-black text-sm tracking-tighter text-teal-500 uppercase">Studio Pro</span>
            <span className="text-[10px] text-slate-500 font-bold uppercase tracking-widest">Emprega Mais RO</span>
          </div>
          <div className="h-8 w-px bg-white/10 mx-2" />
          <div className="flex gap-1 bg-white/5 p-1 rounded-xl border border-white/5">
            <Button variant="ghost" size="sm" onClick={() => dispatch({type: 'UNDO'})} disabled={state.historyIndex <= 0} className="h-8 w-10 hover:bg-white/10 text-slate-400">
              <Undo size={16}/>
            </Button>
            <Button variant="ghost" size="sm" onClick={() => dispatch({type: 'REDO'})} disabled={state.historyIndex >= state.history.length - 1} className="h-8 w-10 hover:bg-white/10 text-slate-400">
              <Redo2 size={16}/>
            </Button>
          </div>
        </div>

        <div className="flex items-center gap-4">
          <Button variant="ghost" onClick={onClose} className="text-slate-400 hover:text-white uppercase text-[10px] font-bold tracking-widest">
            Descartar
          </Button>
          <Button 
            onClick={handleSaveAndExit} 
            className="bg-teal-600 hover:bg-teal-500 text-white font-black uppercase tracking-widest px-10 h-12 shadow-xl shadow-teal-500/20 rounded-xl transition-all active:scale-95 border-none"
          >
            <Check size={20} className="mr-2"/>
            Salvar e Voltar
          </Button>
        </div>
      </header>

      <div className="flex-1 flex overflow-hidden">
        <aside className="w-24 border-r border-white/5 bg-black/40 flex flex-col items-center py-8 gap-8 shrink-0">
          <ToolButton icon={Sliders} label="Cores" active={activeTool === 'filters'} onClick={() => setActiveTool('filters')} />
          <ToolButton icon={Pencil} label="Pincel" active={activeTool === 'draw'} onClick={() => setActiveTool('draw')} />
          <ToolButton icon={TextIcon} label="Texto" active={activeTool === 'text'} onClick={() => setActiveTool('text')} />
          <ToolButton icon={RotateCw} label="Girar" active={activeTool === 'transform'} onClick={() => setActiveTool('transform')} />
          
          <button 
            onMouseDown={() => setShowOriginal(true)} 
            onMouseUp={() => setShowOriginal(false)} 
            onMouseLeave={() => setShowOriginal(false)}
            className="mt-auto p-5 text-slate-500 hover:text-white transition-all bg-white/5 rounded-2xl mb-4"
          >
            <Eye size={24}/>
          </button>
        </aside>

        <main className="flex-1 bg-[#050505] relative flex items-center justify-center p-12 overflow-hidden pointer-events-auto">
          <div 
            className="relative shadow-[0_0_100px_rgba(0,0,0,0.6)] transition-transform duration-300 ease-out"
            style={{ transform: `scale(${state.zoom})` }}
          >
            <canvas 
              ref={canvasRef}
              onPointerDown={handlePointerDown}
              onPointerMove={handlePointerMove}
              onPointerUp={handlePointerUp}
              onPointerLeave={handlePointerUp}
              className="bg-black border border-white/10 cursor-crosshair touch-none"
            />
          </div>

          <div className="absolute bottom-10 bg-black/80 border border-white/10 px-6 py-3 rounded-full flex gap-8 backdrop-blur-2xl shadow-2xl items-center">
            <button onClick={() => dispatch({type: 'SET_ZOOM', payload: Math.max(0.1, state.zoom - 0.1)})} className="text-slate-400 hover:text-teal-500"><Minus size={20}/></button>
            <span className="text-xs font-black text-teal-500 w-16 text-center tabular-nums tracking-widest">{Math.round(state.zoom * 100)}%</span>
            <button onClick={() => dispatch({type: 'SET_ZOOM', payload: Math.min(3, state.zoom + 0.1)})} className="text-slate-400 hover:text-teal-500"><Plus size={20}/></button>
          </div>
        </main>

        <aside className="w-80 border-l border-white/5 bg-black/30 p-6 overflow-y-auto shrink-0 backdrop-blur-sm">
           {activeTool === 'filters' && (
             <div className="space-y-8 animate-in fade-in slide-in-from-right-4">
                <h3 className="text-xs font-black text-teal-500 uppercase tracking-widest border-b border-white/5 pb-4">Presets Studio</h3>
                <div className="grid grid-cols-2 gap-3">
                  {Object.keys(FILTER_PRESETS).map(name => (
                    <button 
                      key={name} 
                      onClick={() => dispatch({type: 'UPDATE_SETTINGS', payload: FILTER_PRESETS[name]})} 
                      className="bg-white/5 border border-white/10 p-4 rounded-xl text-[10px] font-bold uppercase hover:bg-teal-500 hover:text-black transition-all"
                    >
                      {name}
                    </button>
                  ))}
                </div>
                <div className="pt-4 space-y-6">
                   <ControlSlider label="Brilho" value={state.settings.brightness} max={200} onChange={(v) => dispatch({type: 'UPDATE_SETTINGS', payload: {brightness: v}})} />
                   <ControlSlider label="Contraste" value={state.settings.contrast} max={200} onChange={(v) => dispatch({type: 'UPDATE_SETTINGS', payload: {contrast: v}})} />
                   <ControlSlider label="Saturação" value={state.settings.saturate} max={200} onChange={(v) => dispatch({type: 'UPDATE_SETTINGS', payload: {saturate: v}})} />
                   <ControlSlider label="Nitidez (Blur)" value={state.settings.blur} min={0} max={10} onChange={(v) => dispatch({type: 'UPDATE_SETTINGS', payload: {blur: v}})} />
                </div>
             </div>
           )}

           {activeTool === 'draw' && (
             <div className="space-y-8 animate-in fade-in slide-in-from-right-4">
               <div className="flex justify-between items-center border-b border-white/5 pb-4">
                  <h3 className="text-xs font-black text-teal-500 uppercase tracking-widest">Pincel</h3>
                  <div className="flex gap-2 bg-black/50 p-1 rounded-xl border border-white/10">
                    <button onClick={() => setBrushSettings({...brushSettings, mode: 'source-over'})} className={`p-2 rounded-lg transition-all ${brushSettings.mode === 'source-over' ? 'bg-teal-500 text-black shadow-lg' : 'text-slate-500'}`}><Pencil size={16}/></button>
                    <button onClick={() => setBrushSettings({...brushSettings, mode: 'destination-out'})} className={`p-2 rounded-lg transition-all ${brushSettings.mode === 'destination-out' ? 'bg-teal-500 text-black shadow-lg' : 'text-slate-500'}`}><Eraser size={16}/></button>
                  </div>
               </div>
               <ControlSlider label="Tamanho" value={brushSettings.size} min={1} max={150} onChange={(v) => setBrushSettings({...brushSettings, size: v})} />
               <div className="grid grid-cols-4 gap-3">
                 {['#14b8a6', '#f43f5e', '#ffffff', '#000000', '#f59e0b', '#3b82f6', '#a855f7', '#22c55e'].map(c => (
                   <button 
                    key={c} 
                    onClick={() => setBrushSettings({...brushSettings, color: c, mode: 'source-over'})} 
                    className={`aspect-square rounded-full border-4 transition-all hover:scale-110 ${brushSettings.color === c && brushSettings.mode === 'source-over' ? 'border-white shadow-lg shadow-white/20' : 'border-transparent'}`} 
                    style={{backgroundColor: c}} 
                   />
                 ))}
               </div>
             </div>
           )}

           {activeTool === 'text' && (
             <div className="space-y-8 animate-in fade-in slide-in-from-right-4">
                <h3 className="text-xs font-black text-teal-500 uppercase tracking-widest border-b border-white/5 pb-4">Legenda</h3>
                <Input 
                  value={textInput.text} 
                  onChange={(e) => setTextInput({...textInput, text: e.target.value})} 
                  placeholder="Digite aqui..." 
                  className="bg-black border-white/20 h-16 text-lg font-bold focus:border-teal-500 transition-colors"
                />
                <ControlSlider label="Tamanho da Fonte" value={textInput.size} min={10} max={400} onChange={(v) => setTextInput({...textInput, size: v})} />
                <Button 
                  onClick={() => {
                    const canvas = canvasRef.current;
                    const ctx = canvas.getContext('2d');
                    ctx.font = `bold ${textInput.size}px Inter, sans-serif`;
                    ctx.fillStyle = textInput.color;
                    ctx.textAlign = "center";
                    ctx.fillText(textInput.text, canvas.width / 2, canvas.height / 2);
                    dispatch({ type: 'SET_IMAGE', payload: canvas.toDataURL() });
                    setTextInput({...textInput, text: ''});
                    toast.success("Texto aplicado!");
                  }} 
                  className="w-full bg-white text-black font-black uppercase text-xs h-14 rounded-xl shadow-xl hover:bg-slate-200"
                >
                  Carimbar na Imagem
                </Button>
             </div>
           )}

           {activeTool === 'transform' && (
             <div className="space-y-8 animate-in fade-in slide-in-from-right-4">
                <h3 className="text-xs font-black text-teal-500 uppercase tracking-widest border-b border-white/5 pb-4">Orientação</h3>
                <div className="grid grid-cols-1 gap-4">
                   <Button variant="outline" className="h-16 border-white/10 gap-3" onClick={() => dispatch({type: 'TRANSFORM', payload: {rotation: (state.rotation + 90) % 360}})}>
                      <RotateCw size={20}/> Rotacionar 90°
                   </Button>
                   <div className="grid grid-cols-2 gap-4">
                      <Button variant="outline" className="h-16 border-white/10 gap-3" onClick={() => dispatch({type: 'TRANSFORM', payload: {flipH: !state.flipH}})}>
                        <FlipHorizontal size={20}/> Inverter H
                      </Button>
                      <Button variant="outline" className="h-16 border-white/10 gap-3" onClick={() => dispatch({type: 'TRANSFORM', payload: {flipV: !state.flipV}})}>
                        <FlipVertical size={20}/> Inverter V
                      </Button>
                   </div>
                </div>
             </div>
           )}
        </aside>
      </div>
    </div>
  );
}

function ToolButton({ icon: Icon, label, active, onClick }) {
  return (
    <button 
      onClick={onClick} 
      className={`group flex flex-col items-center gap-2 p-4 w-16 h-16 rounded-2xl transition-all ${active ? 'bg-teal-500 text-black shadow-2xl shadow-teal-500/40' : 'text-slate-500 hover:text-white hover:bg-white/5'}`}
    >
      <Icon size={ active ? 22 : 20 } />
      <span className={`text-[7px] font-black uppercase tracking-widest ${active ? 'text-black' : 'text-slate-500'}`}>{label}</span>
    </button>
  );
}

function ControlSlider({ label, value, min = 0, max, step = 1, onChange }) {
  return (
    <div className="space-y-4">
      <div className="flex justify-between text-[9px] font-black uppercase tracking-tighter text-slate-400">
        <span>{label}</span> <span className="text-teal-400 text-xs">{value}</span>
      </div>
      <Slider value={[value]} min={min} max={max} step={step} onValueChange={([v]) => onChange(v)} className="cursor-pointer" />
    </div>
  );
}